import React, {Component} from "react"
import PropTypes from "prop-types"

import History from "@irati/utils/history"
import {H2, H3, Body, Label} from "@irati/ui/src/Typography"
import {FormattedMessage} from "@irati/utils/lang"
import Flex from "@irati/ui/src/Flex"
import Spacing from "@irati/ui/src/Spacing"
import Icon, {IconTypes} from "@irati/ui/src/Icon"
import {ButtonRouterLink, RouterLink} from "@irati/ui/src/Link"
import theme from "@irati/ui/src/global/theme"
import authentication from "@irati/auth"
import adminTracking from "@irati-tracking/admin"
import {getAuthURL} from "@irati/sso/services/sso"
import Button from "@irati/ui/src/Button"
import devToggles from "@irati/dev-toggles"

import Help from "../Help"
import BackToIntegration from "../BackToIntegration"
import EnabledDisabledLabel from "../EnabledDisabledLabel"
import {
  SETTINGS_LOGIN_INTEGRATION,
  SETTINGS_LOGIN_INTEGRATION_CHECKLIST_SSO,
  SETTINGS_LOGIN_INTEGRATION_CUSTOMIZE_SSO
} from "../../../../routes/routeConfig"
import {EnableSSOImage} from "../../../../images/index"
import {InfoCard} from "../styled"
import EditSSO from "./EditSSO"
import SetupInformation from "./SSOSetupInformation"

const SSO_CHECKLIST = "SSO_checklist"

interface Props {}

interface State {
  enabled: boolean
  showSetupInfo: boolean
}

class SingleSignOn extends Component<Props, State> {
  state: State = {
    enabled: false,
    showSetupInfo: false
  }
  static contextTypes = {
    appPath: PropTypes.string
  }

  componentDidMount() {
    getAuthURL(authentication.business().uuid!, undefined, undefined, {fetchPolicy: "network-only"}).then(({url}) => {
      this.setState({enabled: !!url})
    })
    adminTracking.ssoSetup("homepage")
  }

  onClickBack = () => {
    History.push(
      {
        pathname: `${SETTINGS_LOGIN_INTEGRATION}`
      },
      this.context
    )
  }

  onChecklistPage = () => {
    adminTracking.startIntegrationsSSOJourney(SSO_CHECKLIST)
  }

  render() {
    const {enabled, showSetupInfo} = this.state

    if (showSetupInfo) {
      return (
        <div>
          <BackToIntegration onClick={this.onClickBack} />
          <SetupInformation onBack={() => this.setState({showSetupInfo: false})} />
        </div>
      )
    }

    return (
      <>
        <BackToIntegration onClick={this.onClickBack} />
        <Flex>
          <Spacing marginRight={7}>
            <Flex marginBottom={1} alignItems="center">
              <H2 marginRight={1}>
                <FormattedMessage id="admin.components.integration.sso.title" />
              </H2>
              <EnabledDisabledLabel
                enabled={enabled}
                purchased={devToggles.showSSOFeatureFlag() ? !!authentication.apps("admin.singleSignOn") : true}
              />
            </Flex>
            <Body marginBottom={2}>
              <FormattedMessage id="admin.components.integration.sso.text" />
            </Body>
            <Flex marginBottom={1}>
              <RouterLink cta to={SETTINGS_LOGIN_INTEGRATION_CHECKLIST_SSO} onClick={this.onChecklistPage}>
                <FormattedMessage id="admin.components.integration.sso.checklistPageLink" />
              </RouterLink>
              <Icon icon={IconTypes.arrowRight} color={theme.colors.primary1} marginLeft={theme.spacing.one} />
            </Flex>
          </Spacing>
          <EnableSSOImage />
        </Flex>
        <Spacing marginBottom={7} />
        {!enabled && (
          <>
            <InfoCard padding={2} marginBottom={4}>
              <Flex>
                <Spacing>
                  <Icon icon={IconTypes.info} size={20} />
                </Spacing>
                <Body marginLeft={1}>
                  <FormattedMessage
                    id="admin.components.integration.sso.loginHint"
                    values={{
                      bold: (
                        <Label large weight="bold">
                          <FormattedMessage id="admin.components.integration.sso.loginHintBoldWord" />
                        </Label>
                      )
                    }}
                  />
                </Body>
              </Flex>
            </InfoCard>
            <InfoCard padding={4} marginTop={6}>
              <Flex justifyContent="space-between" width="100%">
                <Flex direction="column">
                  <H3 marginBottom={1}>
                    <FormattedMessage id="admin.components.integration.sso.title" />
                  </H3>
                  <Body marginBottom={4}>
                    <FormattedMessage id="admin.components.integration.sso.enableSSO.desc" />
                  </Body>
                  <div>
                    <Button id="settingsIntegrationSSOGetStarted_button" onClick={() => this.setState({showSetupInfo: true})} primary>
                      <FormattedMessage id="admin.dashboard.integrations.card.button" />
                    </Button>
                  </div>
                </Flex>
                <EnableSSOImage />
              </Flex>
            </InfoCard>
          </>
        )}
        {enabled && (
          <>
            <ButtonRouterLink id="settingsIntegrationSSOEditName_button" marginLeft={2} to={SETTINGS_LOGIN_INTEGRATION_CUSTOMIZE_SSO}>
              <FormattedMessage id="admin.components.integration.sso.button.editName" />
            </ButtonRouterLink>
            <EditSSO onDelete={() => this.setState({enabled: false})} />
          </>
        )}
        <Help />
      </>
    )
  }
}

export default SingleSignOn
